/**
 * ReliabilitySummary.jsx — One canonical reliability component for the whole app.
 *
 * Modes:
 *   - mode="compact"  : One-line "Reliability: <Level>" + tooltip.
 *                       Use in Analysis ReportHeader, Dashboard cards, sim list rows.
 *   - mode="card"     : Inline card with overall headline.
 *                       Use in Overview tab.
 *   - mode="full"     : Full panel with 4 pillars + warnings.
 *                       Use in PREAnalytics + HRLabReport "Reliability & Assumptions".
 *
 * Source of truth: buildReliabilitySummary({ simulation, preData, peopleLabStability, diagnostics })
 */
import { useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Shield, AlertTriangle, Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  buildReliabilitySummary,
  PILLAR_META,
  PILLAR_ORDER,
} from '../../lib/reliabilityUtils';

const LEVEL_TONE = {
  high:    { text: 'text-positive-soft-foreground', headline: 'text-positive', badge: 'text-positive-soft-foreground bg-positive-soft border-positive-border', ring: 'border-positive-border dark:border-positive-border/40' },
  medium:  { text: 'text-warning-soft-foreground', headline: 'text-warning', badge: 'text-warning-soft-foreground bg-warning-soft border-warning-border', ring: 'border-warning-border dark:border-warning-border/40' },
  low:     { text: 'text-negative-soft-foreground', headline: 'text-negative', badge: 'text-negative-soft-foreground bg-negative-soft border-negative-border', ring: 'border-negative-border dark:border-negative-border/40' },
  unknown: { text: 'text-slate-600', headline: 'text-slate-500', badge: 'text-slate-600 bg-slate-50 border-slate-200', ring: 'border-slate-100 dark:border-slate-700' },
};

export default function ReliabilitySummary({
  mode = 'full',
  simulation = null,
  preData = null,
  peopleLabStability = null,
  diagnostics = null,
  summary: externalSummary = null,
  className,
  testId = 'reliability-summary',
}) {
  const summary = useMemo(
    () =>
      externalSummary ||
      buildReliabilitySummary({ simulation, preData, peopleLabStability, diagnostics }),
    [externalSummary, simulation, preData, peopleLabStability, diagnostics]
  );

  const tone = LEVEL_TONE[summary.level] || LEVEL_TONE.unknown;

  // ─── COMPACT ─────────────────────────────────────────────────────────────
  if (mode === 'compact') {
    return (
      <Badge
        variant="outline"
        className={cn('text-[11px] font-medium gap-1', tone.badge, className)}
        title={summary.summary}
        data-testid={testId}
      >
        <Shield className="w-3 h-3" />
        Reliability: {summary.label}
        {summary.score != null && (
          <span className="text-slate-500 font-normal">· {summary.score}/100</span>
        )}
      </Badge>
    );
  }

  // ─── CARD ────────────────────────────────────────────────────────────────
  if (mode === 'card') {
    return (
      <Card className={cn('border', tone.ring, className)} data-testid={testId}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className={cn('w-4 h-4 mt-0.5 flex-shrink-0', tone.headline)} />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Report Reliability:
                </p>
                <span className={cn('text-sm font-bold', tone.headline)}>
                  {summary.label}
                  {summary.score != null && (
                    <span className="text-slate-400 font-normal ml-1">· {summary.score}/100</span>
                  )}
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">
                {summary.summary}
              </p>
              <p className="text-[11px] text-slate-400 mt-1">
                Based on outcome mapping, response certainty, segment stability, and data coverage.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── FULL ────────────────────────────────────────────────────────────────
  return (
    <div className={cn('space-y-4', className)} data-testid={testId}>
      {/* Plain-English headline */}
      <div className="p-3 rounded-lg border border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
            <span className="font-medium text-slate-700 dark:text-slate-200">
              {summary.label} report reliability
            </span>
            {summary.score != null && <span className="text-slate-400"> · {summary.score} / 100</span>}
            <span className="block mt-1">{summary.summary}</span>
            <span className="block mt-1 text-[11px] text-slate-400">
              Based on outcome mapping, response certainty, segment stability, and data coverage.
            </span>
          </p>
        </div>
      </div>

      {/* Four pillars */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="reliability-pillars">
        {PILLAR_ORDER.map((key) => (
          <PillarCard
            key={key}
            testid={`reliability-pillar-${key}`}
            title={PILLAR_META[key].title}
            description={PILLAR_META[key].description}
            pillar={summary.pillars[key]}
          />
        ))}
      </div>

      {/* Warnings */}
      {summary.warnings.length > 0 && (
        <div className="space-y-1.5" data-testid="reliability-warnings">
          {summary.warnings.map((w, i) => (
            <p key={i} className="text-xs text-warning-soft-foreground flex items-start gap-2">
              <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              {w.message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function PillarCard({ title, description, pillar, testid }) {
  const tone = LEVEL_TONE[pillar.level] || LEVEL_TONE.unknown;
  const labelMap = { high: 'High', medium: 'Moderate', low: 'Low', unknown: 'Unknown' };

  return (
    <div className={cn('p-3 rounded-lg border', tone.ring)} data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">{title}</p>
        <Badge variant="outline" className={cn('text-[10px] font-medium', tone.headline)}>
          {labelMap[pillar.level] || 'Unknown'}
        </Badge>
      </div>
      <p className={cn('text-xl font-bold tabular-nums', tone.headline)}>
        {pillar.score != null ? `${pillar.score}` : '—'}
        {pillar.score != null && (
          <span className="text-[10px] text-slate-400 font-medium ml-1">/ 100</span>
        )}
      </p>
      <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">{pillar.reason}</p>
      <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">{description}</p>
    </div>
  );
}
